
import React, { useState, useEffect } from "react";
import { Routes, Route, useParams, useLocation, useNavigate } from "react-router-dom";
import "./TenantRoutes.css";
import Sidebar from "../components/Sidebar";
import MemberList from "../components/member/MemberList";
import MemberAdd from "../components/member/MemberAdd";
import Dashboard from "../components/Dashboard";
import MemberDetail from "../components/member/MemberDetail";
import EventList from "../components/event/EventList";
import EventAdd from "../components/event/EventAdd";
import Event from "../components/event/Event";
import EventDetail from "../components/event/EventDetail";
import EventItemAdd from "../components/event/EventItemAdd";
import EventItemDetail from "../components/event/EventItemDetail";
import AppHeader from "../components/AppHeader";
import TaskAdd from "../components/event/TaskAdd";
import EventHub from "../components/event/EventHub";
import EventAction from "../components/event/EventAction";
import EventActionDetail from "../components/event/EventActionDetail";
import EventActionsList from "../components/event/EventActionsList";
import UserAdd from "../components/tenant/UserAdd";
import Integrations from "../components/Integrations";
import AppFooter from "../components/AppFooter";
import MediaPost from "../components/media/MediaPost";
import TenantSettings from './TenantSettings';
import CalendarPage from "../pages/CalendarPage";
import PasswordResetForm from "../components/PasswordResetForm";
import ResetPassword from "../components/ResetPassword";
import { MessagesProvider, useMessagesUI } from "../context/MessagesContext";
import MessageThreadModal from "./messages/MessageThreadModal";
import EventHistory from "./history/EventHistory";
import EventHistoryArticle from "./history/EventHistoryArticle";
import PublicEventHistory from "./history/PublicEventHistory";
import PublicEventArticle from "./history/PublicEventArticle";
import MemberLogin from "./member/MemberLogin";
import TenantUsers from "./tenant/TenantUsers";
import UserDetail from "./tenant/UserDetail";
import SchedulePost from "./media/SchedulePost";
import Raffle from "../components/event/Raffle";
import CollaborationInvitation from "../components/tenant/CollaborationInvitation";
import AuctionRaffleSetup from "./event/AuctionRaffleSetup";
import AuctionConfig from "./event/AuctionConfig";
import RaffleConfig from "./event/RaffleConfig";
import CheckinConsole from "./ticketing/CheckinConsole";
import EventAttendanceAnalytics from "./ticketing/EventAttendanceAnalytics";
import OrdersPanel from "./ticketing/OrdersPanel";
import EventHubDashboard from "./event/EventHubDashboard";
import VendorPanel from "./event/VendorPanel";
import BudgetPanel from "./event/BudgetPanel";
import CampaignList from "./campaign/CampaignList";
import CampaignDetail from "./campaign/CampaignDetail";
import ContentCalendar from "./campaign/ContentCalendar";
import EventPublicSettings from "./event/EventPublicSettings";
import MyTicketsPage from "./member/MyTicketsPage";
import YouTubeIntegration from './YouTubeIntegration';
import YouTubeAnalytics from './YouTubeAnalytics';
import SocialAnalytics from './analytics/SocialAnalytics';
import PostAnalytics from './analytics/PostAnalytics';
import LinkManager from './analytics/LinkManager';
import PromotersPage from './promoters/PromotersPage';
import PromoterLeaderboard from './promoters/PromoterLeaderboard';
import PromoterPortal from './promoters/PromoterPortal';
import PromoterJoin from './promoters/PromoterJoin';
import FormsPage from './forms/FormsPage';
import FormEditor from './forms/FormEditor';
import AudiencePage from './audience/AudiencePage';
import SegmentBuilder from './audience/SegmentBuilder';
import MessageComposer from './audience/MessageComposer';
import MessageReport from './audience/MessageReport';
import SocialInbox from './SocialInbox';
import MediaPostEnhanced from './media/MediaPostEnhanced'
import ScheduledPostsList from "./media/ScheduledPostsList";
import ScheduledEventsList from "./event/ScheduledEventsList";
import TaskList from "./TaskList";
import BillingDashboard from "./payment/BillingDashboard";
import SubscriptionPlans from "./payment/SubscriptionPlans";
import PaymentMethodForm from "./payment/PaymentMethodForm";
import PostsList from "./media/PostsList";
import NewPost from "./media/NewPost";
import TextPost from "./media/TextPost";
import PostDetail from "./media/PostDetail";
import SuperAdminDashboard from "./admin/SuperAdminDashboard";
import TestRunner from "./admin/TestRunner";
import EtsyShop from "./EtsyShop";
import VintedShop from "./VintedShop";
import TenantTransactions from "./settings/TenantTransactions";
import { FaArrowLeft, FaHome } from "react-icons/fa";

// Inline wrapper components for auction and raffle config
const AuctionConfigRoute = () => {
  const { tenant, eventId, itemId } = useParams();
  const navigate = useNavigate();
  
  const handleClose = () => {
    navigate(`/${tenant}/${eventId}/${itemId}/eventItemDetail`);
  };
  
  return (
    <div style={{ position: 'relative', width: '100%', minHeight: '100vh', padding: '20px', paddingBottom: 72, backgroundColor: '#f5f5f5' }}>
      <AuctionConfig
        tenantId={Number(tenant)}
        eventId={Number(eventId)}
        itemId={Number(itemId)}
        itemName=""
        onClose={handleClose}
      />
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(`/${tenant}/${eventId}/${itemId}/eventItemDetail`),
          },
          {
            icon: <FaHome />,
            label: "Home",
            onClick: () => navigate(`/${tenant}/dashboard`),
          },
        ]}
      />
    </div>
  );
};

// Event Hub subsection wrapper: gives the panel a mobile AppFooter (Back to the
// event hub + Home). On desktop, desktop.css re-skins a bare .footer-container
// into the pinned top action strip, so this covers both viewports.
//
// Panels can inject their own footer buttons (e.g. CheckinConsole's Camera/Scan
// controls, only while its Scan tab is active) via the `onExtraFooterButtons`
// callback prop — they get slotted between Back and Home.
const HubSectionRoute = ({ Panel }) => {
  const { tenant, eventId } = useParams();
  const navigate = useNavigate();
  const [extraFooterButtons, setExtraFooterButtons] = useState([]);
  return (
    <div style={{ paddingBottom: 72 }}>
      <Panel
        tenantId={Number(tenant)}
        eventId={Number(eventId)}
        onExtraFooterButtons={setExtraFooterButtons}
      />
      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: "Back", onClick: () => navigate(`/${tenant}/${eventId}/hub`) },
          ...extraFooterButtons,
          { icon: <FaHome />, label: "Home", onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
};

const CheckinConsoleRoute = () => <HubSectionRoute Panel={CheckinConsole} />;
const EventAttendanceAnalyticsRoute = () => <HubSectionRoute Panel={EventAttendanceAnalytics} />;
const OrdersPanelRoute = () => <HubSectionRoute Panel={OrdersPanel} />;
const VendorPanelRoute = () => <HubSectionRoute Panel={VendorPanel} />;
const BudgetPanelRoute = () => <HubSectionRoute Panel={BudgetPanel} />;

// Standalone Transaction Report page, reachable from the Dashboard submenu.
// Same component and API as Settings > Transactions (owner/admin only, tenant
// scoped from the JWT server-side); this just gives it its own route + heading.
const TransactionReportRoute = () => {
  const { tenant } = useParams();
  const navigate = useNavigate();
  return (
    <div style={{ padding: '20px', paddingBottom: 72 }}>
      <h2 style={{ marginBottom: 12 }}>Transaction Report</h2>

      {/* Desktop inline action bar (replaces footer on desktop) */}
      <div className="desktop-action-bar">
        <button type="button" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <button type="button" onClick={() => navigate(`/${tenant}/dashboard`)}>
          <FaHome /> Home
        </button>
      </div>

      <TenantTransactions tenant={tenant} />
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(-1),
          },
          {
            icon: <FaHome />,
            label: "Home",
            onClick: () => navigate(`/${tenant}/dashboard`),
          },
        ]}
      />
    </div>
  );
};

const RaffleConfigRoute = () => {
  const { tenant, eventId, itemId } = useParams();
  const navigate = useNavigate();

  const handleClose = () => {
    navigate(`/${tenant}/${eventId}/${itemId}/eventItemDetail`);
  };

  return (
    <div style={{ position: 'relative', width: '100%', minHeight: '100vh', padding: '20px', paddingBottom: 72, backgroundColor: '#f5f5f5' }}>
      <RaffleConfig
        tenantId={Number(tenant)}
        eventId={Number(eventId)}
        itemId={Number(itemId)}
        itemName=""
        onClose={handleClose}
      />
      <AppFooter
        buttons={[
          {
            icon: <FaArrowLeft />,
            label: "Back",
            onClick: () => navigate(`/${tenant}/${eventId}/${itemId}/eventItemDetail`),
          },
          {
            icon: <FaHome />,
            label: "Home",
            onClick: () => navigate(`/${tenant}/dashboard`),
          },
        ]}
      />
    </div>
  );
};

// Opens a message thread when ?openThread=<id> is in the URL (e.g. from push notification clicks)
const OpenThreadFromQuery = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { openMessages } = useMessagesUI();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const threadId = params.get('openThread');
    if (threadId) {
      openMessages(Number(threadId));
      // Remove the query param so it doesn't re-trigger on navigation
      params.delete('openThread');
      const newSearch = params.toString();
      navigate(
        { pathname: location.pathname, search: newSearch ? `?${newSearch}` : '' },
        { replace: true }
      );
    }
  }, [location.search, openMessages, navigate, location.pathname]);

  return null;
};

const TenantRoutes = () => {
  const { tenant } = useParams();
  const location = useLocation();

  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const toggleSidebar = () => setIsSidebarVisible(v => !v);

  // EventAction route variations — these are PUBLIC-facing capture pages
  // (visitors entering a raffle/auction, etc.). The menu/sidebar must be
  // hidden on every device, mobile and desktop, so the public surface stays
  // clean. Admins manage actions from the admin list at /eventActions
  // (plural), which is NOT matched by this regex.
  const path = location.pathname.toLowerCase();
  const isEventActionRoute = /\/eventaction(\/|$)/.test(path) ||
                              path.match(/\/event\/[^/]+\/item\/[^/]+\/action$/) ||
                              path.match(/\/\d+\/\d+\/action$/);

  const hideHeaderAndSidebar =
    path.includes("/publiceventhistory") ||
    path.includes("/publiceventarticle") ||
    path.includes("/memberlogin") ||
    // Promoter member-portal pages (not the staff /promoters page)
    /\/promoter(\/join)?$/.test(path) ||
    isEventActionRoute;

  // Detect public member pages (MemberAdd, MemberDetail) - show modified header, no sidebar
  const isMemberPublicPage = path.endsWith("/memberadd") ||
                              path.endsWith("/memberdetail");

  // Track desktop breakpoint for always-visible sidebar
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsDesktop(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    setIsSidebarVisible(false);
  }, [location.pathname, location.search, location.hash]);

  return (
    <MessagesProvider>
      <OpenThreadFromQuery />
      <div className={hideHeaderAndSidebar ? "public-page" : "tenant-page"}>
        {!hideHeaderAndSidebar && (
          <div>
            <AppHeader
              isSidebarVisible={isSidebarVisible}
              toggleSidebar={toggleSidebar}
              hideMessagesBell={isMemberPublicPage}
              disableSidebarToggle={isMemberPublicPage}
            />
            {!isMemberPublicPage && (isSidebarVisible || isDesktop) && <Sidebar tenantId={tenant} />}
          </div>
        )}
        <div className="container">
          <div className="tenant-content">
            <Routes>
              <Route path="reset-password" element={<PasswordResetForm tenantId={tenant} />} />
              <Route path="reset-password/:token" element={<ResetPassword tenantId={tenant} />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />

              <Route path="integrations" element={<Integrations tenantId={tenant} />} />
              <Route path="members" element={<MemberList tenantId={tenant} />} />
              <Route path="memberAdd" element={<MemberAdd tenantId={tenant} />} />
              <Route path="memberDetail" element={<MemberDetail tenantId={tenant} />} />
              <Route path="userAdd" element={<UserAdd tenantId={tenant} />} />

              <Route path="events" element={<EventList tenantId={tenant} />} />
              <Route path="eventAdd" element={<EventAdd tenantId={tenant} />} />
              <Route path=":eventId/event" element={<Event tenantId={tenant} />} />
              <Route path=":eventId/eventDetail" element={<EventDetail tenantId={tenant} />} />
              <Route path=":eventId/eventItemAdd" element={<EventItemAdd tenantId={tenant} />} />
              <Route path=":eventId/:itemId/eventItemDetail" element={<EventItemDetail tenantId={tenant} />} />
              <Route path="eventActions" element={<EventActionsList tenantId={tenant} />} />
              <Route path="events/scheduled-events" element={<ScheduledEventsList tenantId={tenant} />} />
              <Route path="tasks" element={<TaskList />} />
              <Route path=":eventId/task" element={<TaskAdd tenantId={tenant} />} />
              <Route path="eventHub" element={<EventHub tenantId={tenant} />} />

              {/* EventAction routes - all variations will hide AppHeader */}
              <Route path="event/:eventId/item/:itemId/action" element={<EventAction tenantId={tenant} />} />
              <Route path=":eventId/:itemId/action" element={<EventAction tenantId={tenant} />} />
              <Route path=":eventId/:itemId/eventAction" element={<EventAction tenantId={tenant} />} />
              <Route path=":eventId/:itemId/eventaction" element={<EventAction tenantId={tenant} />} />

              {/* EventActionDetail - admin interaction for event actions */}
              <Route path=":eventId/:itemId/actionDetail" element={<EventActionDetail tenantId={tenant} />} />

              <Route path="dashboard" element={<Dashboard tenantId={tenant} />} />
              <Route path="calendar" element={<CalendarPage tenantId={tenant} />} />

              <Route path=":eventId/mediaPost" element={<MediaPost tenantId={tenant} />} />
              <Route path=":eventId/:itemId/mediaPost" element={<MediaPost tenantId={tenant} />} />
              <Route path=":eventId/:itemId/mediaPostEnhanced" element={<MediaPostEnhanced tenantId={tenant} />} />

              <Route path="settings" element={<TenantSettings tenantId={tenant} />} />
              <Route path="eventHistory" element={<EventHistory tenantId={tenant} />} />
              <Route path=":eventId/eventHistoryArticle" element={<EventHistoryArticle tenantId={tenant} />} />
              <Route path="publicEventHistory" element={<PublicEventHistory tenantId={tenant} />} />
              <Route path=":eventId/publicEventArticle" element={<PublicEventArticle tenantId={tenant} />} />
              <Route path="memberLogin" element={<MemberLogin tenantId={tenant} />} />
              <Route path="tenantUsers" element={<TenantUsers tenantId={tenant} />} />
              <Route path=":userId/user" element={<UserDetail tenantId={tenant} />} />

              <Route path=":eventId/schedulePost" element={<SchedulePost tenantId={tenant} />} />
              <Route path=":eventId/:itemId/schedulePost" element={<SchedulePost tenantId={tenant} />} />
              <Route path="media/scheduled-posts" element={<ScheduledPostsList tenantId={tenant} />} />
              <Route path="media/schedule" element={<SchedulePost tenantId={tenant} />} />

              <Route path=":eventId/:itemId/raffle" element={<Raffle tenantId={tenant} />} />
              <Route path=":eventId/checkin" element={<CheckinConsoleRoute />} />
              <Route path=":eventId/ticketing-analytics" element={<EventAttendanceAnalyticsRoute />} />
              <Route path=":eventId/orders" element={<OrdersPanelRoute />} />
              <Route path=":eventId/hub" element={<EventHubDashboard />} />
              <Route path=":eventId/public-page" element={<EventPublicSettings tenantId={tenant} />} />
              <Route path=":eventId/vendors" element={<VendorPanelRoute />} />
              <Route path=":eventId/budget" element={<BudgetPanelRoute />} />
              <Route path="my-tickets" element={<MyTicketsPage />} />
              <Route path="promoter" element={<PromoterPortal />} />
              <Route path="promoter/join" element={<PromoterJoin />} />

              <Route path="collaborate" element={<CollaborationInvitation tenantId={tenant} />} />
              <Route path="etsy" element={<EtsyShop tenantId={tenant} />} />
              <Route path="vinted" element={<VintedShop tenantId={tenant} />} />
              <Route path="youtube" element={<YouTubeIntegration tenantId={tenant} />} />
              <Route path="youtube-analytics" element={<YouTubeAnalytics tenantId={tenant} />} />

              {/* Social Analytics */}
              <Route path="analytics" element={<SocialAnalytics tenantId={tenant} />} />
              <Route path="analytics/posts" element={<PostAnalytics tenantId={tenant} />} />
              <Route path="links" element={<LinkManager tenantId={tenant} />} />
              <Route path="promoters" element={<PromotersPage tenantId={tenant} />} />
              <Route path=":eventId/promoters" element={<PromoterLeaderboard tenantId={tenant} />} />
              <Route path="forms" element={<FormsPage tenantId={tenant} />} />
              <Route path="forms/:formId" element={<FormEditor tenantId={tenant} />} />
              <Route path="audience" element={<AudiencePage tenantId={tenant} />} />
              <Route path="audience/segments/:segmentId" element={<SegmentBuilder tenantId={tenant} />} />
              <Route path="audience/messages/new" element={<MessageComposer tenantId={tenant} />} />
              <Route path="audience/messages/:sendId/edit" element={<MessageComposer tenantId={tenant} />} />
              <Route path="audience/messages/:sendId" element={<MessageReport tenantId={tenant} />} />
              <Route path="replies" element={<SocialInbox tenantId={tenant} />} />
              <Route path="transactions" element={<TransactionReportRoute />} />

              {/* Marketing campaigns (On-Screen 2.0 unified Event + Social workflow) */}
              <Route path="campaigns" element={<CampaignList tenantId={tenant} />} />
              <Route path="campaigns/:campaignId" element={<CampaignDetail tenantId={tenant} />} />
              <Route path="campaigns/:campaignId/calendar" element={<ContentCalendar tenantId={tenant} />} />
              <Route path="content-calendar" element={<ContentCalendar tenantId={tenant} />} />

              <Route path=":eventId/:itemId/actionSetup" element={<AuctionRaffleSetup tenantId={tenant} />} />
              <Route path=":eventId/:itemId/auctionConfig" element={<AuctionConfigRoute />} />
              <Route path=":eventId/:itemId/raffleConfig" element={<RaffleConfigRoute />} />

              {/* Billing routes */}
              <Route path="billing" element={<BillingDashboard tenantId={tenant} />} />
              <Route path="billing/subscription" element={<SubscriptionPlans tenantId={tenant} />} />
              <Route path="billing/payment-methods" element={<PaymentMethodForm tenantId={tenant} />} />

              <Route path="post" element={<PostsList tenantId={tenant} />} />
              <Route path="post/new" element={<NewPost tenantId={tenant} />} />
              <Route path="post/text" element={<TextPost tenantId={tenant} />} />
              <Route path="post/:postId" element={<PostDetail tenantId={tenant} />} />

              {/* Super Admin (restricted server-side to SE3D / On-Screen tenants) */}
              <Route path="admin" element={<SuperAdminDashboard tenantId={tenant} />} />
              <Route path="admin/tests" element={<TestRunner tenantId={tenant} />} />
            </Routes>
            
            <MessageThreadModal tenantId={tenant} />
          </div>
        </div>
      </div>
    </MessagesProvider>
  );
};

export default TenantRoutes;